import React, { useState, useEffect, useCallback } from 'react';
import {
  FiAlertTriangle,
  FiX,
  FiRefreshCw,
  FiCloud,
  FiArrowDown,
  FiArrowUp,
  FiExternalLink,
} from 'react-icons/fi';
import { getExternalChanges, resolveBedrockConflict } from '../../../../../services/aiOperationsApi';

interface ExternalChange {
  id: string;
  bedrock_prompt_name?: string;
  aivilo_prompt_name?: string;
  bedrock_prompt_arn?: string;
  bedrock_current_version?: string;
  last_bedrock_modified_at?: string;
}

interface ExternalChangesNotificationProps {
  onResolve?: (mappingId: string, resolution: string) => void;
  className?: string;
}

const ExternalChangesNotification: React.FC<ExternalChangesNotificationProps> = ({ onResolve, className = '' }) => {
  const [changes, setChanges] = useState<ExternalChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [resolving, setResolving] = useState<string | null>(null);

  const fetchChanges = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getExternalChanges();
      setChanges(response.mappings ?? []);
    } catch (err: unknown) {
      console.error('Failed to fetch external changes:', err);
      setChanges([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChanges();
    const interval = setInterval(fetchChanges, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchChanges]);

  const handleResolve = async (mappingId: string, resolution: string) => {
    try {
      setResolving(mappingId);
      await resolveBedrockConflict(mappingId, resolution);
      setChanges(prev => prev.filter(c => c.id !== mappingId));
      if (onResolve) {
        onResolve(mappingId, resolution);
      }
    } catch (err: unknown) {
      console.error('Failed to resolve conflict:', err);
    } finally {
      setResolving(null);
    }
  };

  const handleDismiss = (mappingId: string) => {
    setDismissed(prev => [...prev, mappingId]);
  };

  const visibleChanges = changes.filter(c => !dismissed.includes(c.id));

  if (loading || visibleChanges.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {visibleChanges.map((change) => (
        <div
          key={change.id}
          className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                <FiAlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-medium text-orange-800 dark:text-orange-300">
                    External Changes Detected
                  </h3>
                  <p className="mt-1 text-sm text-orange-700 dark:text-orange-400">
                    <span className="font-medium">{change.bedrock_prompt_name ?? change.aivilo_prompt_name}</span>
                    {' '}was modified directly in AWS Bedrock.
                  </p>
                </div>

                <button
                  onClick={() => handleDismiss(change.id)}
                  className="flex-shrink-0 text-orange-400 hover:text-orange-600 dark:text-orange-500 dark:hover:text-orange-300"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-orange-600 dark:text-orange-400">
                {change.bedrock_prompt_arn && (
                  <div className="flex items-center gap-1">
                    <FiCloud className="w-3.5 h-3.5" />
                    <span className="font-mono truncate max-w-xs">
                      {change.bedrock_prompt_arn.split('/').pop()}
                    </span>
                  </div>
                )}
                {change.bedrock_current_version && (
                  <span>Version: {change.bedrock_current_version}</span>
                )}
                {change.last_bedrock_modified_at && (
                  <span>
                    Changed: {new Date(change.last_bedrock_modified_at).toLocaleString()}
                  </span>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handleResolve(change.id, 'use_bedrock')}
                  disabled={resolving === change.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                           bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300
                           rounded hover:bg-orange-200 dark:hover:bg-orange-900/60
                           disabled:opacity-50 transition-colors"
                >
                  <FiArrowDown className="w-3.5 h-3.5" />
                  {resolving === change.id ? 'Updating...' : 'Use Bedrock Version'}
                </button>

                <button
                  onClick={() => handleResolve(change.id, 'use_aivilo')}
                  disabled={resolving === change.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                           bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300
                           rounded hover:bg-gray-200 dark:hover:bg-gray-600
                           disabled:opacity-50 transition-colors"
                >
                  <FiArrowUp className="w-3.5 h-3.5" />
                  Keep Aivilo Version
                </button>

                {change.bedrock_prompt_arn && (
                  <a
                    href={`https://console.aws.amazon.com/bedrock/home?region=${change.bedrock_prompt_arn.split(':')[3]}#/prompt-management`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                             text-orange-600 dark:text-orange-400 hover:underline"
                  >
                    <FiExternalLink className="w-3.5 h-3.5" />
                    View in AWS
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

interface ExternalChangesBannerProps {
  count: number;
  onClick?: () => void;
  className?: string;
}

export const ExternalChangesBanner: React.FC<ExternalChangesBannerProps> = ({ count, onClick, className = '' }) => {
  if (!count || count === 0) return null;

  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-4 py-2 w-full
        bg-orange-50 dark:bg-orange-900/20
        border border-orange-200 dark:border-orange-800
        rounded-lg text-left
        hover:bg-orange-100 dark:hover:bg-orange-900/30
        transition-colors
        ${className}
      `}
    >
      <FiAlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
          {count} prompt{count === 1 ? '' : 's'} modified externally
        </span>
        <span className="text-xs text-orange-600 dark:text-orange-400 ml-2">
          Click to review
        </span>
      </div>
      <FiRefreshCw className="w-4 h-4 text-orange-400" />
    </button>
  );
};

interface UseExternalChangesResult {
  count: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

export const useExternalChanges = (): UseExternalChangesResult => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getExternalChanges();
      setCount(response.mappings?.length ?? 0);
    } catch (err: unknown) {
      console.error('Failed to fetch external changes:', err);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refresh]);

  return { count, loading, refresh };
};

export default ExternalChangesNotification;
